"use strict";
import {
  SearchTokensResponse
} from "@uniswap/client-search/dist/search/v1/api_pb";
import { Token } from "@uniswap/sdk-core";
import { useMemo } from "react";
import { getTokenLogoUrl } from "constants/tokenLogos";
import { getDexTokenList } from "constants/topazTokenList";
import { ProtectionResult, SafetyLevel } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { UniverseChainId } from "features/chains/types";
import { getCurrencySafetyInfo } from "features/dataApi/utils";
import { currencyId } from "utils/uniswapCurrencyId";
import { isAddress } from "utilities/src/addresses";
function tokenToSearchToken(token, chainId) {
  return {
    chainId,
    address: token.address,
    symbol: token.symbol || "",
    name: token.name || "",
    decimals: token.decimals,
    logoUrl: getTokenLogoUrl(token.address)
  };
}
function _tokenToCurrencyInfo(token) {
  const safetyInfo = getCurrencySafetyInfo(SafetyLevel.Verified, {
    attackTypes: [],
    result: ProtectionResult.Benign
  });
  return {
    currencyId: currencyId(token),
    currency: token,
    logoUrl: getTokenLogoUrl(token.address),
    safetyLevel: SafetyLevel.Verified,
    safetyInfo,
    isSpam: false
  };
}
export function useSearchTokensQuery({
  input,
  enabled = true,
  userAddedTokens = []
}) {
  const searchResponse = useMemo(() => {
    if (!enabled || !input?.searchQuery) {
      return new SearchTokensResponse({ tokens: [] });
    }
    const query = input.searchQuery.toLowerCase();
    const isSearchQueryAnAddress = isAddress(input.searchQuery);
    const resultSize = input.size ?? Number.MAX_SAFE_INTEGER;
    const results = [];
    const chainsToSearch = input.chainIds?.length ? input.chainIds : [UniverseChainId.Mainnet, UniverseChainId.Sepolia];
    chainsToSearch.forEach((chainId) => {
      const chainTokens = getDexTokenList(chainId);
      const userTokensForChain = userAddedTokens.filter((token) => token.chainId === chainId);
      const hardcodedAddresses = new Set(chainTokens.map((token) => token.address.toLowerCase()));
      const uniqueUserTokens = userTokensForChain.filter(
        (token) => !hardcodedAddresses.has(token.address.toLowerCase())
      );
      const allTokens = [...chainTokens, ...uniqueUserTokens];
      const matchingTokens = allTokens.filter((token) => {
        if (isSearchQueryAnAddress) {
          return token.address.toLowerCase() === query;
        }
        return token.symbol?.toLowerCase().includes(query) || token.name?.toLowerCase().includes(query);
      });
      const searchTokens = matchingTokens.map((token) => tokenToSearchToken(token, chainId));
      results.push(...searchTokens);
    });
    const sortedTokens = results.map((token, index) => ({ token, index, score: scoreTokenSearchResult(token, query) })).sort(
      (a, b) => b.score - a.score || a.token.symbol.localeCompare(b.token.symbol) || a.token.name.localeCompare(b.token.name) || a.index - b.index
    ).slice(0, resultSize).map(({ token }) => token);
    return new SearchTokensResponse({ tokens: sortedTokens });
  }, [input, enabled, userAddedTokens]);
  const result = {
    data: searchResponse,
    error: null,
    isError: false,
    isPending: false,
    isLoading: false,
    isLoadingError: false,
    isRefetchError: false,
    isSuccess: true,
    status: "success",
    dataUpdatedAt: Date.now(),
    errorUpdatedAt: 0,
    failureCount: 0,
    failureReason: null,
    errorUpdateCount: 0,
    isFetched: true,
    isFetchedAfterMount: true,
    isFetching: false,
    isInitialLoading: false,
    isPaused: false,
    isPlaceholderData: false,
    isRefetching: false,
    isStale: false,
    fetchStatus: "idle",
    refetch: () => Promise.resolve(result)
  };
  return result;
}
function scoreTokenSearchResult(token, query) {
  const symbol = token.symbol.toLowerCase();
  const name = token.name.toLowerCase();
  const address = token.address.toLowerCase();
  if (address === query) {
    return 1e4;
  }
  if (symbol === query) {
    return 5e3;
  }
  if (name === query) {
    return 4e3;
  }
  if (symbol.startsWith(query)) {
    return 2e3;
  }
  if (name.startsWith(query)) {
    return 1500;
  }
  if (symbol.includes(query)) {
    return 750;
  }
  if (name.includes(query)) {
    return 500;
  }
  return 0;
}
export function searchTokenToCurrencyInfo(token) {
  const { chainId, address, symbol, name, decimals } = token;
  const tokenInstance = new Token(chainId, address, decimals, symbol, name);
  const safetyInfo = getCurrencySafetyInfo(SafetyLevel.Verified, {
    attackTypes: [],
    result: ProtectionResult.Benign
  });
  return {
    currencyId: currencyId(tokenInstance),
    currency: tokenInstance,
    logoUrl: getTokenLogoUrl(address),
    safetyLevel: SafetyLevel.Verified,
    safetyInfo,
    isSpam: false
  };
}
